import type ServiceApi from "../../api";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import {ANONYMOUS_CREDENTIALS} from "../credentials";
import type {ApiExecutor} from "../executor";
import type {PasswordResetRequestPayload} from "../payload-types/password-reset";
import {WebError} from "../web-error";

/**
 * #19351: POST {@link RevaApi.PATH_PASSWORD_RESET_REQUEST} — requests a password-reset e-mail for a login
 * name or e-mail address. Needs no credentials (a user who forgot their password has none), so `execute`
 * is overridden to always supply {@link ANONYMOUS_CREDENTIALS} rather than the caller's session — never a
 * JWT here.
 *
 * Always answers 200 `{}`, whether or not the account/e-mail exists (neutral response, never reveals
 * account existence). 429/503 are rethrown as the raw {@link WebError} instead of being converted to a
 * generic `ErrorI18n` (unlike the base class default), so the caller can show account-management's own
 * `error`/`error_de` message (429, rate limit) resp. the fixed "not reachable" text (503).
 */
export class PasswordResetRequestApiInterface extends SimpleApiInterface<object> {
    constructor(private readonly payload: PasswordResetRequestPayload) {
        super();
    }

    async execute(api: ServiceApi): Promise<object> {
        return super.execute(api, ANONYMOUS_CREDENTIALS);
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([RevaApi.PATH_PASSWORD_RESET_REQUEST]);
    }

    protected buildPayload(): PasswordResetRequestPayload {
        return this.payload;
    }

    protected executeInternal(executor: ApiExecutor, url: string, credentials: ApiCredentials): Promise<object> {
        return executor.fetchPOST(url, credentials, this.buildPayload());
    }

    protected override handleApiErrors(e: Error): object {
        if (e instanceof WebError && (e.status === 429 || e.status === 503)) {
            throw e;
        }
        return super.handleApiErrors(e);
    }
}
